import React, { useEffect, useState } from 'react';
import { Box, Tabs, Tab, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from '@mui/material';
import { Dropzone, FileItem, ExtFile } from '@dropzone-ui/react';
import * as XLSX from 'xlsx';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { createUpload, setCreateUploadStatus } from '../../redux/slices/upload/index';
import { AddButton } from '../button/button';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { AnyAction } from '@reduxjs/toolkit';
import { STATUS } from '../../utils/status';
import SnackbarNotification from '../snackbar/snackbarNotification';
import { RootState } from '../../redux/store/appStore';
import { setUploadBulkQuestionStatus, uploadBulkQuestions } from '../../redux/slices/uploadFile';
import { usePollingTaskStatus } from '../outputJson/usePollingStatus';
import { getOutputJsonTaskStatus, setTaskStatus } from '../../redux/slices/outputJson';
import { setDownloadAnalyticsByExpertStatus } from '../../redux/slices/analytics/experts';
import TaskStatusModal from '../outputJson/taskStatusModal';
import ConfirmationDialog from './confirmationModal';
import DeleteModal from '../modal/deleteModal';
import { bulkDeleteTasks, setBulkDeleteStatus } from '../../redux/slices/checkQualityAcceptedTasks/taskUploadSlice';

interface SheetData {
  name: string;
  data: any[][];
}

const SpreadsheetImportWizard = () => {
  const { createStatus, upload } = useSelector((state: RootState) => state.upload);
  const uploadBulkQuestionMessage = useSelector((state: RootState) => state.uploadFile.uploadBulkQuestionMessage)
  const uploadBulkQuestionStatus = useSelector((state: RootState) => state.uploadFile.uploadBulkQuestionStatus)
  // const updateStatus = useSelector((state: RootState) => state.projects.updateStatus);

  const bulkDeleteStatus = useSelector((state: RootState) => state.uploadTask.bulkDeleteStatus);
  const bulkDeleteMessage = useSelector((state: RootState) => state.uploadTask.message);
  const [files, setFiles] = useState<ExtFile[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [previewTabIndex, setPreviewTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const createUploadMessage = useSelector((state: RootState) => state.upload.createUploadMessage)
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const path = window.location.pathname;
  const isUploadQuestion = path.includes('uploadQuestions');
  const isBulkDelete = path.includes('bulktaskdelete');

  const handleSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleDrop = (newFiles: ExtFile[]) => {
    setFiles(newFiles);
    if (newFiles.length > 0) {
      const file = newFiles[0].file;
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const binaryStr = e.target?.result;
          if (typeof binaryStr === 'string') {
            setLoading(true);
            const workbook = XLSX.read(binaryStr, { type: 'binary' });
            const sheetDataArray = workbook.SheetNames.map(sheetName => {
              const worksheet = workbook.Sheets[sheetName];
              const sheetData = XLSX.utils.sheet_to_json<any[][]>(worksheet, { header: 1 });
              return { name: sheetName, data: sheetData };
            });
            setSheets(sheetDataArray);
            setPreviewTabIndex(0);
            setLoading(false);
          }
        };
        reader.readAsBinaryString(file);
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handlePreviewTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setPreviewTabIndex(newValue);
  };

  const handleNextTab = () => {
    if (tabIndex < 1) {
      setTabIndex(tabIndex + 1);
    }
  };

  const handlePreviousTab = () => {
    if (tabIndex > 0) {
      setTabIndex(tabIndex - 1);
    }
  };

  const handleSubmit = () => {
    if (files.length === 0 || !id) {
      handleSnackbar('Please upload a file to submit', 'warning');
      return;
    }
    const file = files[0].file;
    if (!file) {
      handleSnackbar('Invalid file format or missing file', 'error');
      return;
    }
    if (isUploadQuestion || isBulkDelete) {
      const isXlsx = file.name.toLowerCase().endsWith('.xlsx');
      if (!isXlsx) {
        const context = isBulkDelete ? 'bulk task deletion' : 'bulk question upload';
        handleSnackbar('Only .xlsx files are allowed for bulk question upload', 'error');
        return;
      }
      if (isBulkDelete) {
        dispatch(bulkDeleteTasks({ projectId: id as string, file }) as unknown as AnyAction);
      } else {
        dispatch(uploadBulkQuestions({ id, selectedFile: file }) as unknown as AnyAction);
      }
    } else { 
      dispatch(createUpload({ id, selectedFile: file }) as unknown as AnyAction);
    }
  };
  const { getTaskStatusOutputJson, getTaskStatusOutputJsonMessage, taskStatusOutputJsonResponse } = useSelector((state: RootState) => state.outputJson)
  const [openTaskStatusModal, setOpenTaskStatusModal] = useState(false);

  React.useEffect(() => {
    const status = taskStatusOutputJsonResponse?.data?.status;
    if (status === 'PENDING' || status === 'SUCCESS' || status === 'FAILURE') {
      setOpenTaskStatusModal(true);
    }
  }, [taskStatusOutputJsonResponse]);

  const taskId =
    createStatus === STATUS.SUCESS &&
      upload.data &&
      typeof upload.data === 'object' &&
      'task_id' in upload.data
      ? (upload.data as { task_id: string }).task_id
      : undefined;

  usePollingTaskStatus({
    status: taskStatusOutputJsonResponse?.data?.status,
    taskId,
    onSuccess: () => {
      console.log('Polling finished successfully');
    },
    onFailure: () => {
      console.log('Polling failed');
    },
  });

  React.useEffect(() => {
    if (
      getTaskStatusOutputJson === STATUS.SUCESS &&
      taskStatusOutputJsonResponse?.data?.url
    ) {
      const url = taskStatusOutputJsonResponse.data.url;

      setSnackbarMessage("File Available for download.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      if (typeof url === "string") {
        fetch(url)
          .then((response) => response.blob())
          .then((blob) => {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;

            // Dynamically extract filename from the URL
            const filename = url.split("/").pop() || "downloaded-file";
            link.download = filename;

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
          })
          .catch(() => alert("Failed to download file"));
      }
    }

    if (getTaskStatusOutputJson === STATUS.FAILED) {
      setSnackbarMessage(getTaskStatusOutputJsonMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }

    return () => {
      if (
        getTaskStatusOutputJson === STATUS.SUCESS ||
        getTaskStatusOutputJson === STATUS.FAILED
      ) {
        dispatch(setTaskStatus(STATUS.NOT_STARTED));
      }
    };
  }, [getTaskStatusOutputJson]);


  useEffect(() => {
    if (createStatus === STATUS.SUCESS) {
      // if (typeof analyticsExpertDownload == "string") {
      //   window.open(analyticsExpertDownload, '_blank')
      // }
      setSnackbarMessage(createUploadMessage);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      const taskId = upload.data?.task_id
      if (taskId) {
        dispatch(getOutputJsonTaskStatus({ celery_task_id: taskId }) as unknown as AnyAction);
      }
    }

    if (createStatus == STATUS.FAILED) {
      setSnackbarMessage('Couldn\'t Download File. Please Try Again.')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }

    return () => {
      if (createStatus == STATUS.SUCESS || createStatus == STATUS.FAILED) {
        dispatch(setDownloadAnalyticsByExpertStatus(STATUS.NOT_STARTED))
      }

    }
  }, [createStatus])

  useEffect(() => {
    const handleCreateStatus = () => {
      if (createStatus === STATUS.SUCESS) {
        handleSnackbar(createUploadMessage, 'success');
        // setTimeout(() => {
        //   navigate(`/project/${id}?tab=task`);
        // }, 1000);
      }
      if (createStatus === STATUS.FAILED) {
        handleSnackbar(createUploadMessage, 'error');
      }
      if (uploadBulkQuestionStatus === STATUS.SUCESS) {
        handleSnackbar(uploadBulkQuestionMessage, 'success');
        setTimeout(() => {
          navigate(`/project/${id}?tab=test`);
        }, 1000);
      }
      if (uploadBulkQuestionStatus === STATUS.FAILED) {
        handleSnackbar(uploadBulkQuestionMessage, 'error');
      }
    }
    handleCreateStatus();
    return () => {
      if (createStatus === STATUS.SUCESS || createStatus === STATUS.FAILED) {
        dispatch(setCreateUploadStatus(STATUS.NOT_STARTED));
      }
      if (uploadBulkQuestionStatus === STATUS.SUCESS || uploadBulkQuestionStatus === STATUS.FAILED) {
        dispatch(setUploadBulkQuestionStatus(STATUS.NOT_STARTED));
      }
    };
  }, [createStatus, uploadBulkQuestionStatus]);


useEffect(() => {
  const handleBulkDeleteStatus = () => {
    if (bulkDeleteStatus === STATUS.SUCESS) {
      handleSnackbar(bulkDeleteMessage || 'Tasks deleted successfully', 'success');
      // Clear the uploaded file
      setFiles([]);
      setSheets([]);
      // Optionally navigate or refresh
      setTimeout(() => {
        navigate(`/project/${id}?tab=task`);
      }, 1500);
    }
    if (bulkDeleteStatus === STATUS.FAILED) {
      handleSnackbar(bulkDeleteMessage || 'Failed to delete tasks', 'error');
    }
  };
  
  handleBulkDeleteStatus();
  
  return () => {
    if (bulkDeleteStatus === STATUS.SUCESS || bulkDeleteStatus === STATUS.FAILED) {
      dispatch(setBulkDeleteStatus(STATUS.NOT_STARTED));
    }
  };
}, [bulkDeleteStatus, bulkDeleteMessage]);


  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const convertArray = (arr: any[], length: number) => {
    let gapIndices: any = []
    let lastIndex = 0
    let duplicateArr = arr.concat([])
    arr.map((item, index) => {
      if ((index - lastIndex) <= 1) {
      } else {
        gapIndices.push([lastIndex + 1, index - 1])
      }
      lastIndex = index
    })

    gapIndices.map((item: any) => {
      arr.splice(item[0], item[1] - item[0] + 1, ...(new Array(item[1] - item[0] + 1)))
    })


    if (length == duplicateArr.length) {

    } else {
      arr = [...arr, ...(new Array(length - duplicateArr.length))]
    }

    return arr
  }

  const handleConfirmSubmit = () => {
    setOpenConfirmationDialog(false);
    handleSubmit();
  };

  const handleCancelConfirmation = () => {
    setOpenConfirmationDialog(false);
  };


  return (
    <Box>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        centered
        sx={{
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          "& .MuiTabs-indicator": { backgroundColor: "#3D85C6" },
        }}
      >
        <Tab
          label="Upload & Submit"
          sx={{
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: "bold",
            color: "#333",
            "&.Mui-selected": { color: "#3D85C6" },
          }}
        />
        {/* <Tab
          label="2. Preview & Submit"
          sx={{
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: "bold",
            color: "#333",
            "&.Mui-selected": { color: "#3D85C6" },
          }}
        /> */}
      </Tabs>

      {tabIndex === 0 && (
        <Box
          mt={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
          position="relative"
        >
          <Dropzone
            onChange={handleDrop}
            accept={".csv,.tsv,.xls,.xlsx,.xml"}
            maxFiles={1}
            header={false}
            footer={false}
            style={{
              border: "2px dashed #4a90e2",
              borderRadius: "10px",
              padding: "20px",
              backgroundColor: "#ffffff",
              width: "100%",
              textAlign: "center",
              cursor: "pointer",
            }}
          >
            {files.length === 0 ? (
              <>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      color: "#333",
                    }}
                  >
                    {isBulkDelete ? 'Upload task IDs for deletion' : 'Upload a file here'}
                  </Typography>
                  <Typography variant="body2" sx={{ textTransform: 'none', fontSize: '14px', color: '#333', marginTop: '10px' }}>
                    {isBulkDelete || isUploadQuestion ? 'You can upload .xlsx files only' : 'You can upload .csv,.xls,.xlsx'}
                  </Typography>
                  <AddButton>Choose a file</AddButton>
                </div>
              </>
            ) : (
              files.map((file, index) => (
                <FileItem
                  key={index}
                  file={file.file}
                  onDelete={() => setFiles(files.filter((_, i) => i !== index))}
                />
              ))
            )}
          </Dropzone>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Box mt={2} display="flex" justifyContent="space-around" >
              <Box display="flex" alignItems="center" width="100%">
                <Box flexGrow={1} borderBottom="1px solid black" />
                <Typography variant="body1" mx={2}>
                  OR
                </Typography>
                <Box flexGrow={1} borderBottom="1px solid black" />
              </Box>
            </Box>
            <div style={{ display: 'flex', marginLeft: '10px' }}>
              <AddButton disabled><FileUploadIcon />Create Manually</AddButton>
              {/* <AddButton component="a" href={isUploadQuestion ? "https://data-platform-resources.s3.ap-south-1.amazonaws.com/Test+Upload+Template_20250510Mayh05m1746861695s_sijex.xlsx" : "https://data-platform-resources.s3.ap-south-1.amazonaws.com/web-platform-assets/Task+Upload+Template.xlsx"}><FileDownloadIcon /> Download Template</AddButton> */}
              <AddButton 
                component="a" 
                href={
                  isBulkDelete 
                    ? "https://data-platform-resources.s3.ap-south-1.amazonaws.com/bulk_delete_task+Template.xlsx"
                    : isUploadQuestion 
                      ? "https://data-platform-resources.s3.ap-south-1.amazonaws.com/Test+Upload+Template_20250510Mayh05m1746861695s_sijex.xlsx" 
                      : "https://data-platform-resources.s3.ap-south-1.amazonaws.com/web-platform-assets/Task+Upload+Template.xlsx"
                }
              >
                <FileDownloadIcon /> Download Template
              </AddButton>
            </div>
          </div>
          <br />
          {/* <Box mt={2} display="flex" justifyContent="flex-end" width="100%" sx={{ marginBottom: '10px' }}>
            <AddButton onClick={handleNextTab}>Next</AddButton>
          </Box> */}
          <Box
            mt={2} display="flex" justifyContent="flex-end" width="100%" sx={{ marginBottom: '10px' }}
          >
            <AddButton
              disabled={id ? createStatus == STATUS.PENDING || bulkDeleteStatus === STATUS.PENDING : createStatus == STATUS.PENDING || uploadBulkQuestionStatus === STATUS.PENDING || bulkDeleteStatus === STATUS.PENDING}
              startIcon={id ? (createStatus == STATUS.PENDING || uploadBulkQuestionStatus === STATUS.PENDING || bulkDeleteStatus === STATUS.PENDING ? (<CircularProgress size={17} />) : null) : createStatus == STATUS.PENDING ? (<CircularProgress />) : null}
              onClick={() => setOpenConfirmationDialog(true)}
            >
              {/* {isUploadQuestion ? "Submit Questions" : "Submit Task"} */}
              {isBulkDelete ? "Delete Tasks" : isUploadQuestion ? "Submit Questions" : "Submit Task"}
            </AddButton>
          </Box>
        </Box>
      )}
      {/* {tabIndex === 1 && (
        <Box mt={2}>
          {sheets.length > 0 ? (
            <Box>
              <TableContainer
                component={Paper}
                sx={{ mt: 2, maxHeight: 400 }}
              >
                <Table stickyHeader sx={{ borderCollapse: "collapse" }}>
                  <TableHead>
                    <TableRow>
                      {sheets[previewTabIndex]?.data?.[0]?.map((cell, index) => (
                        <TableCell
                          key={index}
                          sx={{
                            backgroundColor: "#f1f3f4",
                            fontWeight: "bold",
                            border: "1px solid #e0e0e0",
                            textAlign: "center",
                            padding: "8px",
                          }}
                        >
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sheets[previewTabIndex].data
                      .slice(1)
                      .map((row, rowIndex) => {
                        if (
                          row.length > 0 &&
                          sheets[previewTabIndex].data[0].length >= row.length
                        ) {
                          row = convertArray(
                            row,
                            sheets[previewTabIndex].data[0].length
                          );
                        }
                        return (
                          <TableRow key={rowIndex}>
                            {row.map((cell, cellIndex) => (
                              <TableCell
                                key={cellIndex}
                                sx={{
                                  border: "1px solid #e0e0e0",
                                  textAlign: "center",
                                  padding: "8px",
                                }}
                              >
                                {cell}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Tabs
                value={previewTabIndex}
                onChange={handlePreviewTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderTop: 1, borderColor: "divider", mt: 2 }}
              >
                {sheets.map((sheet, index) => (
                  <Tab
                    key={index}
                    label={sheet.name}
                    style={{
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      color: "#333",
                    }}
                    className="Mui-selected"
                  />
                ))}
              </Tabs>
            </Box>
          ) : (
            <Typography variant="body1" color="textSecondary">
              No data to display. Please upload a file first.
            </Typography>
          )}
          <Box
            mt={2}
            display="flex"
            justifyContent="space-between"
            sx={{ marginBottom: "10px" }}
          >
            <AddButton onClick={handlePreviousTab}>Previous</AddButton>
            <AddButton
              disabled={id ? createStatus == STATUS.PENDING : createStatus == STATUS.PENDING || uploadBulkQuestionStatus === STATUS.PENDING}
              startIcon={id ? (createStatus == STATUS.PENDING || uploadBulkQuestionStatus === STATUS.PENDING ? (<CircularProgress size={17} />) : null) : createStatus == STATUS.PENDING ? (<CircularProgress />) : null}
              onClick={handleSubmit}
            >
              {isUploadQuestion ? "Submit Questions" : "Submit Task"}
            </AddButton>
          </Box>
        </Box>
      )} */}
      <SnackbarNotification
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
      {/* <ConfirmationDialog
        open={openConfirmationDialog}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmSubmit}
      /> */}
      {/* <DeleteModal
        open={openConfirmationDialog}
        onCancel={handleCancelConfirmation}
        onConfirm={handleConfirmSubmit}
        deleteText='Upload'
        isButtonLoading={id ? createStatus == STATUS.PENDING || uploadBulkQuestionStatus === STATUS.PENDING : createStatus == STATUS.PENDING || uploadBulkQuestionStatus === STATUS.PENDING}
      /> */}
      <DeleteModal
        open={openConfirmationDialog}
        onCancel={handleCancelConfirmation}
        onConfirm={handleConfirmSubmit}
        deleteText={isBulkDelete ? 'Delete' : 'Upload'}
        isButtonLoading={id ? createStatus == STATUS.PENDING || uploadBulkQuestionStatus === STATUS.PENDING : createStatus == STATUS.PENDING || uploadBulkQuestionStatus === STATUS.PENDING}
      />
      <TaskStatusModal
        open={openTaskStatusModal}
        onClose={() => setOpenTaskStatusModal(false)}
        taskStatusData={taskStatusOutputJsonResponse?.data}
        fileUpload={true}
      />
    </Box>
  );
};

export default SpreadsheetImportWizard;
